<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { getQuoteTemplate } from './quoteTemplates';

const props = defineProps({
  templateKey: { type: String, default: 'classic' },
  title: { type: String, default: '' },
  description: { type: String, default: '' },
  accountName: { type: String, default: '' },
  customerName: { type: String, default: '' },
  currency: { type: String, default: 'USD' },
  validUntil: { type: String, default: '' },
  lineItems: { type: Array, default: () => [] },
  subtotal: { type: Number, default: 0 },
  taxAmount: { type: Number, default: 0 },
  totalAmount: { type: Number, default: 0 },
  termsAndConditions: { type: String, default: '' },
});

const { t } = useI18n();

const template = computed(() => getQuoteTemplate(props.templateKey));

const visibleItems = computed(() => {
  const items = props.lineItems.filter(item => item.name || item.description);
  return items.length
    ? items.slice(0, 4)
    : [{ name: t('QUOTES.TEMPLATES.SAMPLE_SERVICE'), quantity: 1, unitPrice: 0 }];
});

const formatMoney = amount => {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: props.currency || 'USD',
      minimumFractionDigits: 2,
    }).format(Number(amount) || 0);
  } catch {
    return `${props.currency || 'USD'} ${(Number(amount) || 0).toFixed(2)}`;
  }
};
</script>

<template>
  <section :aria-label="t('QUOTES.TEMPLATES.PREVIEW_ARIA')">
    <div class="mb-3 flex items-center justify-between">
      <div>
        <p class="text-sm font-medium text-n-slate-12">
          {{ t('QUOTES.TEMPLATES.PREVIEW_TITLE') }}
        </p>
        <p class="text-xs text-n-slate-10">
          {{ t('QUOTES.TEMPLATES.PREVIEW_HINT') }}
        </p>
      </div>
      <span class="rounded-full bg-n-slate-4 px-2.5 py-1 text-xs font-medium text-n-slate-11">
        {{ template.name }}
      </span>
    </div>

    <Transition name="preview" mode="out-in">
      <article
        :key="template.key"
        class="quote-paper aspect-[0.707] w-full overflow-hidden bg-white text-slate-900 shadow-[0_18px_55px_-28px_rgba(15,23,42,0.45)] ring-1 ring-black/5"
        :class="`template-${template.key}`"
      >
        <div class="quote-accent" />
        <header class="quote-header">
          <div class="quote-brand">{{ accountName || 'Your company' }}</div>
          <div class="quote-kicker">{{ t('QUOTES.TEMPLATES.DRAFT_LABEL') }}</div>
          <h4>{{ title || 'Your proposal title' }}</h4>
          <p v-if="description" class="quote-description">{{ description }}</p>
          <div class="quote-number">{{ t('QUOTES.TEMPLATES.DRAFT_NUMBER') }}</div>
        </header>

        <div class="quote-body">
          <dl class="quote-parties">
            <div>
              <dt>{{ t('QUOTES.TEMPLATES.PREPARED_FOR') }}</dt>
              <dd>{{ customerName || 'Customer name' }}</dd>
            </div>
            <div>
              <dt>{{ t('QUOTES.TEMPLATES.VALID_UNTIL') }}</dt>
              <dd>{{ validUntil || 'Select a date' }}</dd>
            </div>
          </dl>

          <div class="quote-items">
            <div class="quote-items-head">
              <span>{{ t('QUOTES.TEMPLATES.SERVICE') }}</span>
              <span>{{ t('QUOTES.TEMPLATES.QUANTITY') }}</span>
              <span>{{ t('QUOTES.TEMPLATES.AMOUNT') }}</span>
            </div>
            <div v-for="(item, index) in visibleItems" :key="index" class="quote-item">
              <span class="quote-item-copy">
                <strong>{{ item.name || item.description }}</strong>
                <small v-if="item.name && item.description">{{ item.description }}</small>
              </span>
              <span>{{ item.quantity || 1 }}</span>
              <span>{{ formatMoney((item.quantity || 1) * (item.unitPrice || 0)) }}</span>
            </div>
          </div>

          <div class="quote-summary">
            <div>
              <span>{{ t('QUOTES.TEMPLATES.SUBTOTAL') }}</span>
              <span>{{ formatMoney(subtotal) }}</span>
            </div>
            <div>
              <span>{{ t('QUOTES.TEMPLATES.TAX') }}</span>
              <span>{{ formatMoney(taxAmount) }}</span>
            </div>
            <div class="quote-total">
              <span>{{ t('QUOTES.TEMPLATES.TOTAL') }}</span>
              <span>{{ formatMoney(totalAmount) }}</span>
            </div>
          </div>

          <div v-if="termsAndConditions" class="quote-terms">
            <strong>{{ t('QUOTES.FORM.TERMS_LABEL') }}</strong>
            <span>{{ termsAndConditions }}</span>
          </div>

          <footer>{{ t('QUOTES.TEMPLATES.THANK_YOU') }}</footer>
        </div>
      </article>
    </Transition>
  </section>
</template>

<style scoped>
.quote-paper {
  --accent: #7c3aed;
  --ink: #172033;
  --muted: #697386;
  --surface: #f7f5ff;
  position: relative;
  font-family: Inter, ui-sans-serif, system-ui, sans-serif;
}

.quote-accent {
  height: 5px;
  background: var(--accent);
}

.quote-header {
  position: relative;
  padding: 28px 28px 22px;
  border-bottom: 1px solid #e7e9ee;
}

.quote-brand {
  margin-bottom: 28px;
  color: var(--ink);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.04em;
}

.quote-kicker,
.quote-parties dt {
  color: var(--accent);
  font-size: 6px;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.quote-header h4 {
  max-width: 80%;
  margin: 5px 0 0;
  color: var(--ink);
  font-size: 22px;
  font-weight: 650;
  line-height: 1.08;
}

.quote-description {
  max-width: 78%;
  margin: 7px 0 0;
  overflow: hidden;
  color: var(--muted);
  font-size: 7px;
  line-height: 1.45;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.template-executive .quote-description {
  color: #cbd2df;
}

.quote-number {
  position: absolute;
  right: 28px;
  top: 28px;
  color: var(--muted);
  font-size: 7px;
}

.quote-body {
  padding: 22px 28px;
}

.quote-parties {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
  margin: 0 0 24px;
}

.quote-parties dd {
  margin: 5px 0 0;
  color: var(--ink);
  font-size: 9px;
  font-weight: 600;
}

.quote-items-head,
.quote-item {
  display: grid;
  grid-template-columns: 1fr 34px 84px;
  gap: 8px;
  align-items: center;
}

.quote-item-copy {
  min-width: 0;
}

.quote-item-copy strong,
.quote-item-copy small {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.quote-terms {
  margin-top: 16px;
  color: var(--muted);
  font-size: 6px;
  line-height: 1.4;
}

.quote-terms strong,
.quote-terms span {
  display: block;
}

.quote-terms strong {
  margin-bottom: 3px;
  color: var(--ink);
  font-size: 6px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.quote-terms span {
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.quote-item-copy strong {
  font-weight: 650;
}

.quote-item-copy small {
  margin-top: 2px;
  color: var(--muted);
  font-size: 6px;
  line-height: 1.25;
}

.quote-items-head {
  padding: 8px 9px;
  background: var(--surface);
  color: var(--muted);
  font-size: 6px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.quote-item {
  padding: 10px 9px;
  border-bottom: 1px solid #eef0f3;
  color: var(--ink);
  font-size: 8px;
}

.quote-items-head span:nth-child(n + 2),
.quote-item span:nth-child(n + 2) {
  text-align: right;
}

.quote-summary {
  width: 58%;
  margin: 18px 0 0 auto;
  color: var(--muted);
  font-size: 8px;
}

.quote-summary > div {
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
}

.quote-summary .quote-total {
  margin-top: 4px;
  padding-top: 8px;
  border-top: 1px solid #dfe3e8;
  color: var(--ink);
  font-size: 12px;
  font-weight: 750;
}

.quote-total span:last-child {
  color: var(--accent);
}

.quote-body footer {
  position: absolute;
  right: 28px;
  bottom: 22px;
  left: 28px;
  color: var(--muted);
  font-size: 7px;
}

.template-executive {
  --accent: #c59b35;
  --ink: #14213d;
  --muted: #657085;
  --surface: #f4f0e6;
  font-family: Georgia, 'Times New Roman', serif;
}

.template-executive .quote-accent {
  height: 0;
}

.template-executive .quote-header {
  background: #14213d;
  border-bottom-color: #c59b35;
}

.template-executive .quote-brand,
.template-executive .quote-header h4 {
  color: #fff;
}

.template-executive .quote-kicker,
.template-executive .quote-number {
  color: #e7c86f;
}

.template-studio {
  --accent: #e45b45;
  --ink: #25201f;
  --muted: #756965;
  --surface: #fff0eb;
}

.template-studio .quote-accent {
  position: absolute;
  inset: 0 auto 0 0;
  width: 11px;
  height: auto;
}

.template-studio .quote-header,
.template-studio .quote-body {
  padding-left: 38px;
}

.template-studio .quote-header h4 {
  max-width: 92%;
  font-size: 25px;
  letter-spacing: -0.04em;
}

.template-minimal {
  --accent: #111827;
  --ink: #111827;
  --muted: #737b87;
  --surface: #fff;
}

.template-minimal .quote-accent {
  height: 0;
}

.template-minimal .quote-header {
  padding-top: 42px;
  border-bottom: 2px solid #111827;
}

.template-minimal .quote-brand {
  margin-bottom: 42px;
}

.template-minimal .quote-items-head {
  border-bottom: 1px solid #111827;
}

.preview-enter-active,
.preview-leave-active {
  transition:
    opacity 150ms ease,
    transform 150ms ease;
}

.preview-enter-from,
.preview-leave-to {
  opacity: 0;
  transform: translateY(4px) scale(0.99);
}
</style>
